import type { CSSProperties } from "react"
import {
  IconAlertTriangle,
  IconCircleCheck,
  IconCircleX,
  IconInfoCircle,
} from "@tabler/icons-react"
import { Toaster as Sonner } from "sonner"

import { useTheme } from "@/components/theme-provider"
import { Spinner } from "@/components/ui/spinner"

/**
 * Toasts confirm something the user JUST did — a change saved, a node
 * paused. A 380px surface card with shadow-md, stacked bottom-right; the
 * tone shows in the icon only. One action at most (Undo, Download). Write
 * the result, not the attempt: "Node mn-fra-03 is draining".
 */
export function Toaster() {
  const { theme } = useTheme()
  return (
    <Sonner
      theme={theme}
      position="bottom-right"
      closeButton
      icons={{
        success: <IconCircleCheck size={16} stroke={1.8} className="text-ok" />,
        error: <IconCircleX size={16} stroke={1.8} className="text-fault" />,
        warning: <IconAlertTriangle size={16} stroke={1.8} className="text-warn" />,
        info: <IconInfoCircle size={16} stroke={1.8} className="text-trace" />,
        loading: <Spinner size="sm" />,
      }}
      style={
        {
          "--normal-bg": "var(--surface)",
          "--normal-text": "var(--ink)",
          "--normal-border": "var(--line)",
          "--border-radius": "10px",
          "--width": "380px",
          "--font-family": "var(--font-sans)",
        } as CSSProperties
      }
      toastOptions={{
        classNames: {
          toast:
            "font-sans! text-[13.5px]! leading-5! shadow-[0_0_0_1px_var(--line),var(--elev-md)]! border-0! items-start! gap-2.5!",
          title: "font-semibold! text-ink!",
          description: "text-ink-muted!",
          icon: "mt-0.5!",
          actionButton:
            "bg-surface! text-ink! shadow-[0_0_0_1px_var(--line),var(--elev-xs)]! rounded-sm! h-7! px-2.5! text-[13px]! font-[550]!",
          cancelButton: "bg-transparent! text-ink-muted!",
          closeButton:
            "bg-surface! text-ink-muted! border-line! hover:text-ink!",
        },
      }}
    />
  )
}
